import { useState } from 'react';
import { BrandsService } from '../../services/shared/BrandsService';
import { SearchResult } from '../../types/header/SearchResult';

export const useSearchDeals = (): SearchDealsType => {
  // set the key to use when saving search history to local storage
  const LOCAL_STORAGE_KEY_SEARCH_HISTORY: string = 'automotivemastermind.recent-searches';

  // get the search history from local storage
  const getSearchHistory = (): string[] => {
    const searchHistory = localStorage.getItem(LOCAL_STORAGE_KEY_SEARCH_HISTORY);
    return searchHistory !== null ? JSON.parse(searchHistory) : '';
  };

  // set the search history in local storage
  const saveSearchHistory = (history: Array<string>): void =>
    localStorage.setItem(LOCAL_STORAGE_KEY_SEARCH_HISTORY, JSON.stringify(history));

  const [searchHistory, setSearchHistory] = useState<string[]>(getSearchHistory() || []);

  const getName = (item: SearchResult): string => {
    return `${item.firstName} ${item.lastName}`.replace('null', '');
  };

  const saveSearchToHistory = (term: string): void => {
    if (term) {
      // prevent duplicate terms from existing in searchHistory[]
      const searchHistoryUpdated = searchHistory.filter((t) => t !== term);

      // add the term to the beginning of searchHistory[]
      searchHistoryUpdated.unshift(term);

      // limit terms[] to 10 items
      if (searchHistoryUpdated.length === 10) {
        searchHistoryUpdated.pop();
      }

      // update local storage
      setSearchHistory(searchHistoryUpdated);
      saveSearchHistory(searchHistoryUpdated);
    }
  };

  const clearHistory = () => {
    // remove all searches from the search history
    const updatedSearchHistory = searchHistory.splice(0, searchHistory.length);

    // update the search history in local storage
    saveSearchHistory(updatedSearchHistory);
  };

  const formatTimeGap = (daysDiff: number): string => {
    // daysDiff will be null if there were no logs found for the deactivated user
    if (daysDiff === null) {
      return '';
    }

    if (daysDiff > 30) {
      return ` ${Math.ceil(daysDiff / 30)} months ago`;
    }
    return ` ${daysDiff} days ago`;
  };

  const isTrimAndYearValid = (item: SearchResult): boolean => {
    return !!item.trim && item.year !== 0;
  };

  const updateMakeName = (make: string): string => {
    return BrandsService.updateMakeName(make);
  };

  const isDealerOptIn = (dealerId: string): boolean => {
    const dealerInfos = JSON.parse(localStorage.getItem('dealerInfos'));
    const dealersList = dealerInfos ? dealerInfos.filter((e) => e.brandName != 'Off-Brand') : [];

    if (dealersList.length > 0) {
      return dealersList.find((x) => x.dealerId == dealerId)?.isDealerOptIn;
    }

    return false;
  };

  const onPrivateIncentiveDealerCheck = (result: any): boolean => {
    if (result.isPrivateIncentiveApplied) {
      if (result.isConquest) {
        if (isDealerOptIn(result.dealerId)) {
          return true;
        } else {
          return false;
        }
      }
      return true;
    }
    return false;
  };

  const hasPrivateIncentive = (): boolean => {
    const isPrivateIncentive = JSON.parse(localStorage.getItem('hasPrivateIncentive'));
    return isPrivateIncentive;
  };

  return {
    searchHistory,
    getName,
    saveSearchToHistory,
    updateMakeName,
    isTrimAndYearValid,
    formatTimeGap,
    clearHistory,
    isDealerOptIn,
    onPrivateIncentiveDealerCheck,
    hasPrivateIncentive,
  };
};

type SearchDealsType = {
  searchHistory: string[];
  getName: (item: SearchResult) => string;
  saveSearchToHistory: (term: string) => void;
  updateMakeName: (make: string) => string;
  isTrimAndYearValid: (item: SearchResult) => boolean;
  formatTimeGap: (daysDiff: number) => string;
  clearHistory: () => void;
  isDealerOptIn: (dealerId: string) => boolean;
  onPrivateIncentiveDealerCheck: (result: any) => boolean;
  hasPrivateIncentive: () => boolean;
};
